import { CopyableText, Dialog, useNotifications } from '@ijoin/components';
import { usePatchAdminLoginResetPassword } from '@ijoin/hooks';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';

import { useEffect, useState } from 'react';

type ResetLinkDialogProps = {
  isOpen: boolean;
  loginId: string;
  onClose: () => void;
  username: string;
};

/** copy-and-deliver counterpart to the send-email reset flow */
export const ResetLinkDialog = (props: ResetLinkDialogProps) => {
  const notifications = useNotifications();
  const [resetLink, setResetLink] = useState<null | string>(null);

  const generateLink = usePatchAdminLoginResetPassword({
    mutation: {
      onError: () => {
        notifications.error('Failed to generate reset link');
        props.onClose();
      },
      onSuccess: response => {
        if (response?.resetLink) {
          setResetLink(response.resetLink);
          notifications.success('Reset link generated');
        }
      }
    }
  });

  useEffect(() => {
    if (!props.isOpen) return;

    setResetLink(null);
    generateLink.mutate({
      data: { mode: 'link' as const },
      loginId: props.loginId
    });
  }, [generateLink.mutate, props.isOpen, props.loginId]);

  const onClose = () => {
    setResetLink(null);
    props.onClose();
  };

  return (
    <Dialog
      data-testid='resetLinkDialog'
      isOpen={props.isOpen}
      onClose={onClose}>
      <DialogTitle variant='h3'>Generate Reset Link</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2'>
            One-time password reset link for {props.username}. Copy it and send
            it to the advisor yourself. No email is sent.
          </Typography>

          {resetLink ? (
            <Stack
              alignItems='center'
              direction='row'
              spacing={1}
              sx={{ minWidth: 0 }}>
              <Typography
                data-testid='generatedResetLink'
                sx={{ wordBreak: 'break-all' }}
                variant='body2'>
                {resetLink}
              </Typography>
              <CopyableText value={resetLink} />
            </Stack>
          ) : (
            <Stack alignItems='center' direction='row' spacing={1}>
              <CircularProgress size={16} />
              <Typography color='text.secondary' variant='body2'>
                Generating link…
              </Typography>
            </Stack>
          )}

          <DialogActions sx={{ p: 0 }}>
            <Button
              data-testid='closeResetLink'
              onClick={onClose}
              variant='contained'>
              Close
            </Button>
          </DialogActions>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
